import { create } from 'zustand';
import type { AuthUserPublic, MeResponse } from '@stfors/shared-types';

// `MeResponse.user` is nullable on the wire (the bootstrap-friendly
// /api/me returns `user: null` when there's no session). Inside the
// store we only hold a Session — the invariant "we have a logged-in
// user" is encoded in the type, so consumers can read `me.user.foo`
// without a per-call null check after gating on `me`.
export type Session = Omit<MeResponse, 'user'> & { user: AuthUserPublic };

interface AuthState {
  me: Session | null;
  loading: boolean;
  setMe: (me: Session | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  me: null,
  loading: true, // bootstrap call decides
  setMe: (me) => set({ me, loading: false }),
  setLoading: (loading) => set({ loading }),
}));

// Mirrors the API-side hasPermission semantics from rbac-and-permissions.md §8.
// Project-level denial > project-level grant > global grant.
export function can(perm: string, projectId?: number): boolean {
  const me = useAuthStore.getState().me;
  if (!me) return false;
  if (projectId !== undefined) {
    const override = me.project_overrides[String(projectId)];
    if (override?.denied.includes(perm)) return false;
    if (override?.granted.includes(perm)) return true;
  }
  return me.global_permissions.includes(perm);
}
